<template>
    <span class="svg-icon">
        <component v-if="isElicon" :is="iconName" :style="{ width, height, color: iconColor }" />
        <span v-else-if="isFontsIcon" :class="`iconfont ${iconName}`" :style="{ fontSize: width }" />
        <img
            v-else-if="isImageType"
            :src="imageSrc"
            :alt="imgAlt"
            :style="{ width, height }"
            :class="{ 'offline-image': isOfflineImage }"
        >
        <svg v-else :style="{ width, height }">
            <use :href="prefix + iconName" :fill="iconColor" />
        </svg>
    </span>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { buildFileSrcUrl, fetchAuthFileObjectUrl } from '@/utils/authFileUrl'

defineOptions({
    name: 'BaseIcon'
})

const props = defineProps({
    prefix: {
        type: String,
        default: '#icon-'
    },
    // svg 名称或图片地址（与 fileId 二选一时常用作非服务端文件场景）
    name: {
        type: String as () => string | undefined
    },
    /**
     * 服务端文件记录 ID：内部经 {@link buildFileSrcUrl} 拼展示地址后按鉴权拉流，优先级高于 name
     */
    fileId: {
        type: String as () => string | undefined
    },
    color: {
        type: String
    },
    width: {
        type: String,
        default: '20px'
    },
    height: {
        type: String,
        default: '20px'
    },
    /** 为 true 且为网络图片时，经统一鉴权拉流后用 blob URL 展示 */
    withAuth: {
        type: Boolean,
        default: false
    },
    /**
     * 普通公网图片（http/https）：直连 `<img src>`，不走鉴权拉流。
     * 仅对 `name` 传入的地址生效；若使用 `fileId` 则仍走服务端受保护下载，忽略本项。
     */
    direct: {
        type: Boolean,
        default: false
    },
    /** 图片模式下的 img alt */
    alt: {
        type: String,
        default: ''
    }
})

const imgAlt = computed(() => props.alt || '')

/** 实际参与类型判断与展示的源：fileId → 展示用 https；否则为 name */
const displaySource = computed(() => {
    const fid = typeof props.fileId === 'string' ? props.fileId.trim() : ''
    if (fid) {
        return buildFileSrcUrl(fid)
    }
    return props.name ?? ''
})

/** 有 fileId 时始终鉴权拉流；仅 name 时 direct 为直连公网图，否则看 withAuth */
const needAuthFetch = computed(() => {
    const hasFileId = typeof props.fileId === 'string' && props.fileId.trim() !== ''
    if (hasFileId) {
        return true
    }
    if (props.direct) {
        return false
    }
    return props.withAuth
})

const isElicon = ref(false)
const isFontsIcon = ref(false)
const isImageType = ref(false)
const isOfflineImage = ref(false)
const authorizedBlobSrc = ref<string | undefined>()
let revokedBlobUrl: string | null = null

const revokeAuthorizedBlob = () => {
    if (revokedBlobUrl) {
        URL.revokeObjectURL(revokedBlobUrl)
        revokedBlobUrl = null
    }
    authorizedBlobSrc.value = undefined
}

const ICON_REGEX = {
    ELEMENT: /^element-/,
    ICONFONT: /^iconfont-/,
    /** http(s) 任意路径；或协议相对 //host/... */
    HTTP_IMAGE: /^https?:\/\/.*\.(png|jpe?g|webp)|^https?:\//i,
    PROTOCOL_RELATIVE: /^\/\/[^\s/]+/,
    BLOB_IMAGE: /^blob:/i,
    FILE_IMAGE: /^file:(?:\/\/)?[^ ].*\.(png|jpe?g|webp)$/i
}

const judgeIconType = (name: string) => {
    isElicon.value = false
    isFontsIcon.value = false
    isImageType.value = false
    isOfflineImage.value = false
    if (ICON_REGEX.ELEMENT.test(name)) {
        isElicon.value = true
        return
    }

    if (ICON_REGEX.ICONFONT.test(name)) {
        isFontsIcon.value = true
        return
    }

    if (ICON_REGEX.HTTP_IMAGE.test(name)) {
        isImageType.value = true
        return
    }

    if (ICON_REGEX.PROTOCOL_RELATIVE.test(name)) {
        isImageType.value = true
        return
    }

    if (ICON_REGEX.BLOB_IMAGE.test(name)) {
        isImageType.value = true
        return
    }

    if (ICON_REGEX.FILE_IMAGE.test(name)) {
        isImageType.value = true
        isOfflineImage.value = true
    }
}

const iconName = computed(() => {
    const name = displaySource.value
    if (!name) {
        return
    }

    const typeHandlers = {
        elicon: () => name.substring(7),
        fonticon: () => name.substring(9),
        image: () => name
    }

    if (isElicon.value) {
        return typeHandlers.elicon()
    }
    if (isFontsIcon.value) {
        return typeHandlers.fonticon()
    }
    if (isImageType.value) {
        return typeHandlers.image()
    }

    return name
})

const iconColor = computed(() => props.color || 'currentColor')

const imageSrc = computed(() => {
    const name = displaySource.value
    if (!name) {
        return
    }
    if (isImageType.value && needAuthFetch.value) {
        if (authorizedBlobSrc.value) {
            return authorizedBlobSrc.value
        }
        /** 协议相对地址无法参与当前鉴权拉流条件，直接交给浏览器解析 */
        if (/^\/\//.test(name)) {
            return name
        }
        return undefined
    }
    if (!isOfflineImage.value) {
        return name
    }
    return new URL(name.replace(/file:/, '../../'), import.meta.url).href
})

watchEffect(() => {
    const val = displaySource.value
    if (val) {
        judgeIconType(val)
    } else {
        isElicon.value = false
        isFontsIcon.value = false
        isImageType.value = false
        isOfflineImage.value = false
    }
})

watchEffect(onCleanup => {
    const name = displaySource.value
    if (!name || !needAuthFetch.value || !isImageType.value || isOfflineImage.value || !/^https?:\/\//i.test(name)) {
        revokeAuthorizedBlob()
        return
    }
    const fetchKey = name
    let cancelled = false
    const run = async () => {
        try {
            const objectUrl = await fetchAuthFileObjectUrl(name)
            if (cancelled || displaySource.value !== fetchKey) {
                URL.revokeObjectURL(objectUrl)
                return
            }
            revokeAuthorizedBlob()
            revokedBlobUrl = objectUrl
            authorizedBlobSrc.value = objectUrl
        } catch {
            if (!cancelled && displaySource.value === fetchKey) {
                authorizedBlobSrc.value = undefined
            }
        }
    }
    void run()
    onCleanup(() => {
        cancelled = true
        revokeAuthorizedBlob()
    })
})
</script>

<style lang="scss" scoped>
.svg-icon {
    @include box-flex(auto, auto, row, center, center);

    transition: all 0.3s;
    border-radius: 50%;
}
</style>
